import { makeAutoObservable, reaction, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { numericUserId } from "../../../auth/userId";
import type {
  CapabilityReadiness,
  CapabilityRecord,
} from "../SystemHealth/capabilityReadiness";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * Wire shapes of the two API services behind Admin → Email
 * (packages/api/src/services/system/email/email-admin.ts). Mirrored by hand,
 * as every other admin view here is.
 */
export interface EmailConfigState {
  configured: boolean;
  problem: string | null;
  apiKeyPresent: boolean;
  apiKeyShaped: boolean;
  fromAddress: string | null;
  fromName: string;
  replyTo: string | null;
  sandbox: boolean;
  sandboxNote: string | null;
}

export type ProviderStatus = "queued" | "accepted" | "refused" | "error";

export interface EmailSendRecord {
  id: number;
  kind: string;
  rail: string;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  fromAddress: string;
  replyTo: string | null;
  subject: string;
  documentId: string | null;
  providerStatus: ProviderStatus | string;
  providerStatusCode: number | null;
  providerMessageId: string | null;
  error: string | null;
  sandbox: boolean;
  requestedByUserId: number | null;
  requestedByApiKeyId: number | null;
  correctsMessageId: number | null;
  createdAt: string | null;
}

export interface TestSendOutcome {
  providerStatus: "accepted" | "refused" | "error";
  accepted: boolean;
  sandbox: boolean;
  problem: string | null;
}

/** The readiness card this screen is the remediation for. */
export const EMAIL_CAPABILITY_KEY = "email.sendgrid";

export type StatusFilter = "all" | ProviderStatus;

/**
 * What each `providerStatus` is CALLED on this screen.
 *
 * `accepted` IS NOT "SENT" AND NEVER "DELIVERED". SendGrid answers 202 when it
 * takes custody; delivery, bounce and complaint happen later and HQ has no event
 * webhook to hear about them (email.provider.ts). A green "Sent" chip over a
 * 202 is the confident-and-wrong signal the ledger column was named
 * `providerStatus` to prevent, so the label carries the caveat instead.
 */
export const PROVIDER_STATUS_META: Record<
  ProviderStatus,
  { label: string; color: "success" | "error" | "warning" | "default" }
> = {
  accepted: { label: "Accepted by SendGrid", color: "success" },
  refused: { label: "Refused", color: "error" },
  error: { label: "Transport error", color: "warning" },
  queued: { label: "Queued", color: "default" },
};

/**
 * Admin → Email: the configuration state, the readiness verdict it feeds, and
 * the `email_sends` ledger.
 *
 * Route ViewStore — a plain class, per the web rules. THE SAME DELIBERATE
 * DEVIATION AS AuditLogStore: `rows` and `total` are the find RESPONSE for the
 * page on screen, not the ServiceStore's entity cache, because the ledger is
 * server-paged and the cache would union every page ever fetched under a count
 * that describes one of them.
 *
 * THREE READS, THREE ERRORS. The config, the readiness verdict and the ledger
 * come from three services and can fail independently; one error field for all
 * three would either blank the parts that loaded or show a failure under a card
 * that is fine. "Not loaded", "failed" and "empty" are kept apart for each.
 */
class EmailAdminStore {
  rootStore: RootStore;

  config: EmailConfigState | null = null;
  configError: string | null = null;

  readiness: CapabilityRecord | null = null;
  readinessAt: string | null = null;
  readinessError: string | null = null;

  rows: EmailSendRecord[] = [];
  total = 0;
  ledgerLoaded = false;
  ledgerError: string | null = null;
  loading = false;

  statusFilter: StatusFilter = "all";
  page = 0;
  pageSize = 50;

  testSending = false;
  testResult: TestSendOutcome | null = null;
  testError: string | null = null;

  /** Last read wins: a slow page must not land over a newer filter's answer. */
  private loadSeq = 0;
  private testSeq = 0;
  /**
   * The query `rows` ANSWER. A failed read keeps the rows on screen only when
   * they answer the query that failed (a refresh of the same page): after a
   * failed filter or page change the old rows would sit under the NEW toggle —
   * accepted sends listed under "Refused" — so they are dropped instead, and
   * the error stands alone.
   */
  private rowsQuery: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // A ViewStore outlives a sign-out (the RootStore is not rebuilt), and this
    // one holds every address HQ has mailed. The next person on a shared
    // browser must start empty, admin or not. A METHOD, not a getter, for the
    // reason TicketDeskStore gives: a getter read here becomes a computed the
    // reaction keeps alive.
    reaction(
      () => this.currentUserId(),
      (id, previous) => {
        if (id !== previous) this.reset();
      },
    );
  }

  private currentUserId(): number | null {
    return numericUserId(
      (
        this.rootStore as unknown as {
          authStore?: { currentUser?: { id?: unknown } };
        }
      ).authStore?.currentUser?.id,
    );
  }

  reset() {
    this.loadSeq += 1;
    this.testSeq += 1;
    this.config = null;
    this.configError = null;
    this.readiness = null;
    this.readinessAt = null;
    this.readinessError = null;
    this.rows = [];
    this.total = 0;
    this.rowsQuery = null;
    this.ledgerLoaded = false;
    this.ledgerError = null;
    this.loading = false;
    this.statusFilter = "all";
    this.page = 0;
    this.testSending = false;
    this.testResult = null;
    this.testError = null;
  }

  // ---- services --------------------------------------------------------------

  private store(name: string): ServiceStoreType | null {
    return (
      ((this.rootStore as unknown as Record<string, unknown>)[name] as
        | ServiceStoreType
        | undefined) ?? null
    );
  }

  /**
   * The library ServiceStore SWALLOWS a transport error into `errorOn<Method>`
   * and resolves null — so "nothing came back" is a failure, and this is where
   * its reason is read.
   */
  private rawSends(): { create(data: object): Promise<unknown> } | null {
    const app = (this.rootStore as unknown as {
      app?: { service?: (p: string) => { create(data: object): Promise<unknown> } };
    }).app;
    return app?.service?.("email-sends") ?? null;
  }

  private swallowed(
    service: ServiceStoreType | null,
    method: "Find" | "Create",
    fallback: string,
  ): string {
    const err = (
      service as unknown as Record<string, { message?: unknown } | null> | null
    )?.[`errorOn${method}`];
    return err && typeof err.message === "string" && err.message
      ? err.message
      : fallback;
  }

  // ---- derived ---------------------------------------------------------------

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / Math.max(1, this.pageSize)));
  }

  /**
   * Accepted rows on this page that were SANDBOXED — accepted, and delivered to
   * nobody. Counted so the screen can say it above the table instead of leaving
   * it to a chip on each row.
   */
  get sandboxedOnPage(): number {
    return this.rows.filter((r) => r.sandbox && r.providerStatus === "accepted")
      .length;
  }

  get query(): Record<string, unknown> {
    const query: Record<string, unknown> = {
      $limit: this.pageSize,
      $skip: this.page * this.pageSize,
    };
    if (this.statusFilter !== "all") query.providerStatus = this.statusFilter;
    return query;
  }

  // ---- actions ---------------------------------------------------------------

  setStatusFilter(value: StatusFilter) {
    if (this.statusFilter === value) return;
    this.statusFilter = value;
    this.page = 0;
    void this.loadLedger();
  }

  setPage(page: number) {
    this.page = Math.max(0, page);
    void this.loadLedger();
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 0;
    void this.loadLedger();
  }

  dismissTestResult() {
    this.testResult = null;
    this.testError = null;
  }

  async load() {
    await Promise.all([this.loadConfig(), this.loadReadiness(), this.loadLedger()]);
  }

  async loadConfig() {
    const service = this.store("emailConfigStore");
    const who = this.currentUserId();
    // A read the sign-out ended RESOLVES null (throwOnError off): ask the
    // session, so the swallowed "Signed out before …" is nobody's error.
    const ended = sessionMark(this.rootStore);
    try {
      const res = (await service?.find({})) as unknown as
        | EmailConfigState
        | null
        | undefined;
      if (this.currentUserId() !== who || ended()) return;
      runInAction(() => {
        if (!res || typeof res !== "object" || !("configured" in res)) {
          this.configError = this.swallowed(
            service,
            "Find",
            "The email configuration could not be read.",
          );
          return;
        }
        this.config = res;
        this.configError = null;
      });
    } catch (err) {
      if (this.currentUserId() !== who || isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.configError =
          (err as Error)?.message || "The email configuration could not be read.";
      });
    }
  }

  /**
   * The `email.sendgrid` readiness verdict — the card whose remediation text
   * sends an operator here. Read from the same report System Health shows, so
   * the two screens cannot disagree about it.
   */
  async loadReadiness() {
    const service = this.store("capabilityReadinessStore");
    const who = this.currentUserId();
    // A read the sign-out ended RESOLVES null (throwOnError off): ask the
    // session, so the swallowed "Signed out before …" is nobody's error.
    const ended = sessionMark(this.rootStore);
    try {
      const res = (await service?.find({})) as unknown as
        | CapabilityReadiness
        | null
        | undefined;
      if (this.currentUserId() !== who || ended()) return;
      runInAction(() => {
        if (!res || !Array.isArray(res.capabilities)) {
          this.readinessError = this.swallowed(
            service,
            "Find",
            "The readiness check could not be read.",
          );
          return;
        }
        const record =
          res.capabilities.find((c) => c.key === EMAIL_CAPABILITY_KEY) ?? null;
        this.readiness = record;
        this.readinessAt = res.generatedAt ?? null;
        // A report that does not carry the card is an answer, but not the one
        // this screen was asked for — say so rather than show nothing.
        this.readinessError = record
          ? null
          : `The readiness report has no "${EMAIL_CAPABILITY_KEY}" check.`;
      });
    } catch (err) {
      if (this.currentUserId() !== who || isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.readinessError =
          (err as Error)?.message || "The readiness check could not be read.";
      });
    }
  }

  async loadLedger() {
    const service = this.store("emailSendsStore");
    const seq = ++this.loadSeq;
    const who = this.currentUserId();
    // A read the sign-out ended RESOLVES null (throwOnError off): ask the
    // session, so the swallowed "Signed out before …" is nobody's error.
    const ended = sessionMark(this.rootStore);
    const query = this.query;
    const queryKey = JSON.stringify(query);
    // Rows that answer a different query must not outlive a failure to answer this one.
    const dropStaleRows = () => {
      if (this.rowsQuery === queryKey) return;
      this.rows = [];
      this.total = 0;
      this.rowsQuery = null;
    };
    runInAction(() => {
      this.loading = true;
    });
    try {
      const res = (await service?.find({ query })) as unknown as
        | { data?: EmailSendRecord[]; total?: number }
        | null
        | undefined;
      if (seq !== this.loadSeq || this.currentUserId() !== who || ended()) return;
      runInAction(() => {
        if (!res || !Array.isArray(res.data)) {
          // The page on screen is NOT the answer for this filter; keep it
          // marked as failed rather than replacing it with an empty table
          // that reads as "nothing was ever sent".
          this.ledgerError = this.swallowed(
            service,
            "Find",
            "The send ledger could not be loaded.",
          );
          dropStaleRows();
          this.ledgerLoaded = true;
          return;
        }
        this.rows = res.data;
        this.total = typeof res.total === "number" ? res.total : res.data.length;
        this.rowsQuery = queryKey;
        this.ledgerError = null;
        this.ledgerLoaded = true;
      });
    } catch (err) {
      if (seq !== this.loadSeq || this.currentUserId() !== who || isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.ledgerError =
          (err as Error)?.message || "The send ledger could not be loaded.";
        dropStaleRows();
        this.ledgerLoaded = true;
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Mail one test message to the signed-in admin's own address.
   *
   * The API takes no recipient (it is not a relay) and ledgers the attempt
   * whatever happens, so afterwards the ledger AND the readiness card are
   * re-read: an accepted test under the current settings is exactly the
   * evidence the card's "Send a test from Admin → Email" asks for.
   */
  async sendTest(): Promise<boolean> {
    if (this.testSending) return false;
    const service = this.store("emailSendsStore");
    // THE RAW SERVICE FIRST. `create({ test: true })` answers `{ outcome, send }`
    // — no `id` — and the ServiceStore wrapper writes every create response into
    // its cache, so each test left an entity keyed "undefined" in
    // `emailSendsStore`. The raw client caches nothing and throws a refusal in
    // the server's words. The wrapper stays the fallback (tests, a missing app).
    const raw = this.rawSends();
    if (!service && !raw) return false;
    const token = ++this.testSeq;
    const who = this.currentUserId();
    // A read the sign-out ended RESOLVES null (throwOnError off): ask the
    // session, so the swallowed "Signed out before …" is nobody's error.
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.testSending = true;
      this.testResult = null;
      this.testError = null;
    });
    try {
      const res = (await (raw ?? service)!.create({ test: true })) as unknown as
        | { outcome?: TestSendOutcome }
        | null
        | undefined;
      if (token !== this.testSeq || this.currentUserId() !== who || ended()) return false;
      if (!res?.outcome) {
        runInAction(() => {
          this.testError = this.swallowed(
            service,
            "Create",
            "The test message could not be requested.",
          );
        });
        return false;
      }
      runInAction(() => {
        this.testResult = res.outcome ?? null;
      });
      await Promise.all([this.loadLedger(), this.loadReadiness()]);
      return res.outcome.accepted;
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      if (token === this.testSeq && this.currentUserId() === who) {
        runInAction(() => {
          this.testError =
            (err as Error)?.message || "The test message could not be requested.";
        });
      }
      return false;
    } finally {
      if (token === this.testSeq) {
        runInAction(() => {
          this.testSending = false;
        });
      }
    }
  }
}

export default EmailAdminStore;
